import { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { calendarService, taskService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import {
  IAttemptListItemResponseForWorkOrder,
  IUserListItemResponse,
} from "@miplatform/types";
import { useCalendarContext } from "../../../providers";
import { IAssignableUser } from "../NhcCalendar.types";
import { buildUnassignedWorkOrdersFilters } from "./buildUnassignedWorkOrdersFilters";

export const MAX_ACTIVE_ASSIGNABLE_USERS = 3;

function withActiveAssignableUserAppended(
  prev: string[],
  userId: string
): string[] {
  if (prev.includes(userId)) {
    return prev;
  }
  if (prev.length >= MAX_ACTIVE_ASSIGNABLE_USERS) {
    return prev;
  }
  return [...prev, userId];
}

export function useNHCPriorityScheduler() {
  const stateManager = useComponentState();
  const workOrdersStateManager = useComponentState();
  const {
    assignableUsers,
    date,
    calendarSet,
    tenantHolidays,
    calendarConfig,
    activeCalendarId,
    events,
  } = useCalendarContext();
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const [assignableUsersAvailableTime, setAssignableUsersAvailableTime] =
    useState<IAssignableUser[]>([]);

  const [activeAssignableUserIds, setActiveAssignableUserIds] = useState<
    string[]
  >([]);

  const [boardExpandedUserId, setBoardExpandedUserId] = useState<string | null>(
    null
  );
  const isHoliday = useMemo(() => {
    if (
      tenantHolidays.some(({ startDate, endDate }) =>
        date.isBetween(startDate, endDate, "day", "[]")
      )
    ) {
      return true;
    }

    return false;
  }, [tenantHolidays, date]);

  const [rawUnassignedWorkOrders, setRawUnassignedWorkOrders] = useState<
    IAttemptListItemResponseForWorkOrder[]
  >([]);

  const templateIdsKey = useMemo(
    () =>
      (calendarConfig?.workTemplateBases ?? [])
        .map((template) => template.id)
        .join(","),
    [calendarConfig?.workTemplateBases]
  );

  const loadWorkOrders = useCallback(() => {
    workOrdersStateManager.run(async () => {
      const templates = calendarConfig?.workTemplateBases ?? [];
      const eventType = calendarSet?.config?.eventType ?? "";

      const filters = buildUnassignedWorkOrdersFilters(templates, eventType);

      const response = await taskService.attempts.getAllWorkOrders({
        filters,
        skip: 0,
        sort: ["dueDate", "home/communityName"],
      });
      if (response.status === HTTP_STATUSES.OK) {
        const data = response.data ?? [];
        setRawUnassignedWorkOrders(
          data.filter(
            (workOrder: IAttemptListItemResponseForWorkOrder) =>
              !workOrder?.assignedUserId
          )
        );
      } else {
        setRawUnassignedWorkOrders([]);
      }
    });
  }, [calendarConfig?.workTemplateBases, calendarSet?.config?.eventType]);

  useEffect(() => {
    loadWorkOrders();
  }, [loadWorkOrders, activeCalendarId, templateIdsKey, events]);

  const expandedUsers = useMemo(
    () =>
      activeAssignableUserIds.reduce<IUserListItemResponse[]>((acc, id) => {
        const user = assignableUsers?.find((u) => u.id === id);
        if (user) acc.push(user);
        return acc;
      }, []),
    [assignableUsers, activeAssignableUserIds]
  );

  const effectiveSelectedUserId = useMemo(() => {
    if (
      selectedUserId == null ||
      activeAssignableUserIds.length === 0 ||
      !activeAssignableUserIds.includes(selectedUserId)
    ) {
      return undefined;
    }
    return selectedUserId;
  }, [selectedUserId, activeAssignableUserIds]);

  const handleAddAssignableUser = useCallback((userId: string) => {
    setActiveAssignableUserIds((prev) =>
      withActiveAssignableUserAppended(prev, userId)
    );
  }, []);

  const handleCloseUserColumn = useCallback(
    (userId: string) => {
      const closingSelectedUser = userId === selectedUserId;

      setActiveAssignableUserIds((prev) => {
        if (!prev.includes(userId)) {
          return prev;
        }
        const idx = prev.indexOf(userId);
        const next = prev.filter((id) => id !== userId);

        if (closingSelectedUser) {
          if (next.length === 0) {
            setSelectedUserId(null);
          } else {
            const nextSelected = idx > 0 ? prev[idx - 1] : next[0];
            if (nextSelected !== undefined) {
              setSelectedUserId(nextSelected);
            }
          }
        }

        return next;
      });
      setBoardExpandedUserId((prev) => (prev === userId ? null : prev));
    },
    [selectedUserId]
  );

  const handleBoardExpandedChange = useCallback(
    (userId: string, expanded: boolean) => {
      setBoardExpandedUserId(expanded ? userId : null);
    },
    []
  );

  const handleUserSelect = useCallback((userId: string) => {
    setActiveAssignableUserIds((prev) => {
      if (prev.includes(userId)) {
        setSelectedUserId(userId);
        return prev;
      }
      const next = withActiveAssignableUserAppended(prev, userId);
      if (next !== prev) {
        setSelectedUserId(userId);
      }
      return next;
    });
  }, []);

  const getAssignableUsersAvailableTime = async () => {
    stateManager.run(async () => {
      if (!calendarSet?.config.id) {
        throw new Error("Calendar set not found");
      }
      const dateString = moment(date).format("YYYY-MM-DD[T]00:00:00.0000000Z");
      const response =
        await calendarService.calendarSets.getAssignableUsersAvailableTime({
          id: calendarSet?.config.id,
          params: {
            date: dateString,
          },
        });
      if (response.status === HTTP_STATUSES.OK) {
        setAssignableUsersAvailableTime(
          response.data?.map((user: IAssignableUser) => ({
            id: user.id,
            name: user.name ?? "",
            availableTime: isHoliday ? 0 : (user.availableTime ?? 0) / 3600,
            assignedWorkOrdersCount: user.assignedWorkOrdersCount ?? 0,
          })) ?? []
        );
      }
    });
  };
  return {
    activeAssignableUserIds,
    boardExpandedUserId,
    effectiveSelectedUserId,
    expandedUsers,
    handleAddAssignableUser,
    handleBoardExpandedChange,
    handleCloseUserColumn,
    handleUserSelect,
    getAssignableUsersAvailableTime,
    isLoading: stateManager.isBusy(),
    isWorkOrdersLoading: workOrdersStateManager.isBusy(),
    assignableUsersAvailableTime,
    rawUnassignedWorkOrders,
    reloadUnassignedWorkOrders: loadWorkOrders,
  };
}
